import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { autoDateFormat } from "./autoDateFormate";
import { formatAmount } from "./formatAmount";

const cacheKey = (type, id) => `txn_detail:${type}:${id}`;

export function cacheTxnDetail(type, id, payload) {
  try {
    sessionStorage.setItem(cacheKey(type, id), JSON.stringify(payload));
  } catch {
    /* ignore */
  }
}

export function readCachedTxnDetail(type, id) {
  try {
    const raw = sessionStorage.getItem(cacheKey(type, id));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** Normalize any listing row into a printable/viewable document model */
export function buildTxnDocument(row, type = "recharge", brand = {}) {
  if (!row) return null;
  const ps = row.paysprintResponse || row.settlementResponse || {};
  const companyName = brand.companyName || "EzyPayInd";
  const legalName = brand.legalName || "";
  const logoUrl = brand.logoUrl || "";

  const common = {
    type,
    id: row._id || row.id || row.txnId,
    companyName,
    legalName,
    logoUrl,
    title: "Transaction Receipt",
    subtitle: String(type).replace(/_/g, " ").toUpperCase(),
    txnId: row.orderId || row.txnId || ps.txnid || ps.refid || "—",
    amount: row.amount ?? row.txnAmount ?? 0,
    status: row.status || row.transactionStatus || "—",
    date: row.createdAt || row.date || row.updatedAt,
    dateLabel: autoDateFormat(row.createdAt || row.date || row.updatedAt) || "—",
  };

  let fields = [];

  if (type === "bbps") {
    const last4 = row.last4DigitCardNumber
      ? `XXXX ${String(row.last4DigitCardNumber).slice(-4)}`
      : "—";
    fields = [
      { label: "Date", value: common.dateLabel },
      { label: "Order ID", value: row.orderId || row.txnId || "—" },
      { label: "Txn ID", value: row.txnId || row.orderId || "—" },
      { label: "API Txn ID", value: row.apiTxnId || "—" },
      { label: "Operator Ref", value: row.operatorRefId || "—" },
      { label: "Reference", value: row.referenceId || "—" },
      { label: "Card", value: last4 },
      { label: "Customer", value: row.customerName || "—" },
      { label: "Mobile", value: row.mobile || "—" },
      { label: "Biller ID", value: row.billerId || "—" },
      { label: "Bill amount", value: `₹ ${formatAmount(row.billAmount ?? 0)}` },
      { label: "Paid amount", value: `₹ ${formatAmount(row.amount ?? 0)}` },
      { label: "Charges", value: `₹ ${formatAmount(row.charges ?? 0)}` },
      { label: "Total debit", value: `₹ ${formatAmount(row.totalDebit ?? row.amount ?? 0)}` },
      { label: "Due date", value: row.dueDate || "—" },
      { label: "Bill date", value: row.billDate || "—" },
      { label: "Provider", value: `${row.providerCode || "WINGPAY"} ${row.providerVersion || "v1"}` },
      { label: "Status", value: common.status },
      { label: "Message", value: row.failureReason || row.wingpayResponse?.message || "—" },
      { label: "Merchant", value: row.userId?.userName || row.userId?.name || "—" },
    ];
  } else if (type === "recharge") {
    fields = [
      { label: "Date", value: common.dateLabel },
      { label: "Txn ID", value: common.txnId },
      { label: "Ref Number", value: ps.refid || ps.operatorid || "—" },
      { label: "Ack Number", value: ps.ackno || "—" },
      { label: "Mobile / CA", value: row.mobile || row.canumber || "—" },
      { label: "Operator", value: row.operator || "—" },
      { label: "Category", value: row.category || "—" },
      { label: "Circle", value: row.circle || "—" },
      { label: "Amount", value: `₹ ${formatAmount(common.amount)}` },
      { label: "Status", value: common.status },
      { label: "Message", value: ps.message || row.errorReason || "—" },
      { label: "Merchant", value: row.userId?.userName || row.userId?.name || "—" },
    ];
  } else if (type === "dmt") {
    fields = [
      { label: "Date", value: common.dateLabel },
      { label: "Txn ID", value: common.txnId },
      { label: "UTR", value: row.utr || ps.utr || "—" },
      { label: "Account", value: row.accountNumber || "—" },
      { label: "IFSC", value: row.ifsc || "—" },
      { label: "Bank", value: row.beneficiaryId?.bankName || row.bankName || "—" },
      { label: "Beneficiary", value: row.beneficiaryId?.name || row.beneName || row.name || "—" },
      { label: "Amount", value: `₹ ${formatAmount(common.amount)}` },
      { label: "Charges", value: row.charges != null ? `₹ ${formatAmount(row.charges)}` : "—" },
      { label: "Mode", value: row.mode || "—" },
      { label: "Status", value: common.status },
      { label: "Merchant", value: row.merchantName || row.merchantCode || "—" },
    ];
  } else if (type === "settlement") {
    fields = [
      { label: "Date", value: common.dateLabel },
      { label: "Txn ID", value: common.txnId || row.utrNumber || "—" },
      { label: "Account", value: row.accountNumber || "—" },
      { label: "IFSC", value: row.ifsc || "—" },
      { label: "Bank", value: row.bankAccountId?.bankName || "—" },
      { label: "Holder", value: row.accountHolderName || "—" },
      { label: "UTR", value: row.utrNumber || row.rawResponse?.utrNumber || "—" },
      { label: "Amount", value: `₹ ${formatAmount(common.amount)}` },
      { label: "Charges", value: row.charges != null ? `₹ ${formatAmount(row.charges)}` : "—" },
      { label: "Status", value: common.status },
      { label: "Merchant", value: row.userId?.name || row.userId?.userName || "—" },
    ];
  } else if (type === "aeps") {
    fields = [
      { label: "Date", value: common.dateLabel },
      { label: "Txn ID", value: common.txnId },
      { label: "Ack No", value: row.psResponse?.ackno || "—" },
      { label: "Aadhaar", value: row.aadhaarLast4 ? `XXXX-XXXX-${row.aadhaarLast4}` : row.aadhaar || "—" },
      { label: "Mobile", value: row.mobile || "—" },
      { label: "Bank", value: row.bankName || row.bank || "—" },
      { label: "Pipe", value: row.pipe || "—" },
      { label: "Amount", value: `₹ ${formatAmount(common.amount)}` },
      { label: "Charges", value: row.charges != null ? `₹ ${formatAmount(row.charges)}` : "—" },
      { label: "Type", value: row.txnType || row.type || "—" },
      { label: "RRN", value: row.psResponse?.bankrrn || "—" },
      { label: "Status", value: common.status },
    ];
  } else if (type === "addmoney") {
    fields = [
      { label: "Date", value: common.dateLabel },
      { label: "Txn ID", value: common.txnId || row?.paymentGatway?.orderId || "—" },
      { label: "Order ID", value: row?.paymentGatway?.orderId || "—" },
      { label: "RRN", value: row?.paymentGatway?.rrn || "—" },
      { label: "Amount", value: `₹ ${formatAmount(common.amount)}` },
      { label: "Status", value: common.status },
      { label: "Gateway", value: row?.paymentGatway?.gateway || row.gateway || "—" },
    ];
  } else {
    fields = [
      { label: "Date", value: common.dateLabel },
      { label: "Txn ID", value: common.txnId },
      { label: "Amount", value: `₹ ${formatAmount(common.amount)}` },
      { label: "Status", value: common.status },
    ];
  }

  // Drop empty-looking fields but keep zeros
  fields = fields.filter((f) => f.value !== undefined && f.value !== null && f.value !== "");

  return { ...common, fields, raw: row };
}

export function buildReceiptHtml(doc) {
  if (!doc) return "";
  const rows = (doc.fields || [])
    .map(
      (f) => `
      <div class="row">
        <span class="label">${escapeHtml(f.label)}</span>
        <span class="value">${escapeHtml(String(f.value))}</span>
      </div>`
    )
    .join("");

  return `
  <div class="receipt-doc" id="receipt-root">
    <div class="header">
      ${
        doc.logoUrl
          ? `<img class="logo" src="${escapeAttr(doc.logoUrl)}" alt="${escapeAttr(
              doc.companyName
            )}" />`
          : `<div class="logo-text">${escapeHtml(doc.companyName)}</div>`
      }
      <h1>${escapeHtml(doc.companyName)}</h1>
      ${doc.legalName ? `<p class="legal">${escapeHtml(doc.legalName)}</p>` : ""}
      <h2>${escapeHtml(doc.title)}</h2>
      <p class="subtitle">${escapeHtml(doc.subtitle)}</p>
    </div>
    <div class="amount-box">
      <div class="amount-label">Amount</div>
      <div class="amount-value">₹ ${escapeHtml(formatAmount(doc.amount))}</div>
      <div class="status status-${escapeAttr(String(doc.status).toLowerCase())}">${escapeHtml(
        String(doc.status)
      )}</div>
    </div>
    <div class="fields">${rows}</div>
    <p class="footer">This is a system generated receipt. No signature required.</p>
  </div>`;
}

export function receiptPrintStyles() {
  return `
    * { box-sizing: border-box; }
    body { margin: 0; padding: 16px; font-family: Poppins, Arial, sans-serif; color: #0F172A; background: #fff; }
    .receipt-doc { max-width: 420px; margin: 0 auto; border: 1px dashed #94a3b8; padding: 16px; border-radius: 4px; }
    .header { text-align: center; margin-bottom: 12px; }
    .logo { max-height: 48px; max-width: 160px; object-fit: contain; margin: 0 auto 8px; display: block; }
    .logo-text { font-size: 18px; font-weight: 700; color: #02026E; margin-bottom: 6px; }
    h1 { font-size: 15px; margin: 0 0 2px; }
    .legal { font-size: 10px; color: #64748b; margin: 0 0 8px; }
    h2 { font-size: 14px; margin: 8px 0 2px; }
    .subtitle { font-size: 11px; color: #64748b; margin: 0; text-transform: uppercase; letter-spacing: 0.04em; }
    .amount-box { text-align: center; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 4px; padding: 10px; margin: 12px 0; }
    .amount-label { font-size: 11px; color: #64748b; }
    .amount-value { font-size: 22px; font-weight: 700; margin: 4px 0; }
    .status { display: inline-block; font-size: 11px; font-weight: 700; padding: 2px 8px; border-radius: 3px; background: #e2e8f0; }
    .status-success { background: #d1fae5; color: #059669; }
    .status-failed { background: #fee2e2; color: #dc2626; }
    .status-pending { background: #fef3c7; color: #d97706; }
    .row { display: flex; justify-content: space-between; gap: 12px; font-size: 12px; padding: 5px 0; border-bottom: 1px solid #f1f5f9; }
    .label { color: #64748b; font-weight: 500; }
    .value { color: #0F172A; font-weight: 600; text-align: right; word-break: break-all; }
    .footer { text-align: center; font-size: 10px; color: #94a3b8; margin-top: 14px; }
    @media print { body { padding: 0; } .receipt-doc { border: none; max-width: 100%; } }
  `;
}

export function printTxnDocument(doc) {
  const html = buildReceiptHtml(doc);
  const win = window.open("", "_blank", "width=480,height=720");
  if (!win) return false;
  win.document.write(`<!doctype html><html><head><title>${doc.txnId || "Receipt"}</title>
    <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700&display=swap" rel="stylesheet">
    <style>${receiptPrintStyles()}</style></head><body>${html}
    <script>window.onload=function(){setTimeout(function(){window.print();},250);}</script>
    </body></html>`);
  win.document.close();
  return true;
}

export async function downloadTxnPdf(doc, element) {
  const hasRoot = element?.querySelector?.("#receipt-root");
  let created = false;
  let target = hasRoot ? element : null;
  if (!target) {
    target = document.createElement("div");
    target.style.position = "fixed";
    target.style.left = "-10000px";
    target.style.top = "0";
    target.innerHTML = `<style>${receiptPrintStyles()}</style>${buildReceiptHtml(doc)}`;
    document.body.appendChild(target);
    created = true;
  }

  const canvas = await html2canvas(target.querySelector?.("#receipt-root") || target, {
    scale: 2,
    useCORS: true,
    backgroundColor: "#ffffff",
    allowTaint: true,
  });

  if (created && target.parentNode) {
    target.parentNode.removeChild(target);
  }

  const imgData = canvas.toDataURL("image/png");
  const pdf = new jsPDF("p", "mm", "a4");
  const pageW = pdf.internal.pageSize.getWidth();
  const margin = 12;
  const usableW = pageW - margin * 2;
  const imgH = (canvas.height * usableW) / canvas.width;
  pdf.addImage(imgData, "PNG", margin, margin, usableW, imgH);
  pdf.save(`${doc.txnId || "receipt"}.pdf`);
  return true;
}

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function escapeAttr(str) {
  return escapeHtml(str).replace(/'/g, "&#39;");
}
